import { useKeyboardControls, useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { CuboidCollider, RigidBody, useRapier } from "@react-three/rapier";
// import { useControls } from "leva";
import { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { useVehicleController } from "./use-vehicle-controller";
import { useGame } from "./stores/useGame";
import CarShadow from "./components/car/CarShadow.jsx";
import { MODELS } from "./components/loader/models.js";
import useDeviceType from "./components/hooks/useDeviceType";

const spawn = {
  position: [0, 0, -12],
  rotation: [0, Math.PI / 2, 0],
};

const startRacePosition = {
  position: [-20, 0, 14],
  rotation: [0, -Math.PI / 2, 0],
};

const radiusWheel = 0.27;
const wheelInfo = {
  axleCs: new THREE.Vector3(0, 0, -1),
  suspensionRestLength: 0.125,
  suspensionStiffness: 24,
  maxSuspensionTravel: 1,
  radius: radiusWheel,
};

const wheelX = 1;
const wheelY = 0.15;
const wheelZ = 0.5;

const wheels = [
  // front
  { position: new THREE.Vector3(-wheelX, -wheelY, -wheelZ), ...wheelInfo },
  { position: new THREE.Vector3(-wheelX, -wheelY, wheelZ), ...wheelInfo },
  // rear
  { position: new THREE.Vector3(wheelX - 0.1, -wheelY, -wheelZ), ...wheelInfo },
  { position: new THREE.Vector3(wheelX - 0.1, -wheelY, wheelZ), ...wheelInfo },
];

const _airControlAngVel = new THREE.Vector3(0, 0, 0);

export default function Car({
  position,
  rotation,
  initialRacePosition,
  initialRaceRotation,
}) {
  const { world, rapier } = useRapier();
  const [, getKeyboardControls] = useKeyboardControls();
  const deviceType = useDeviceType();
  const [smoothCameraPosition] = useState(() => new THREE.Vector3(10, 10, 10));
  const [smoothCameraTarget] = useState(() => new THREE.Vector3());
  const carMovedToStartLocation = useGame(
    (state) => state.carMovedToStartLocation
  );

  const chasisMeshRef = useRef(null);
  const chasisBodyRef = useRef(null);
  const wheelsRef = useRef([]);
  const decelerationTimer = useRef(0);

  const { vehicleController } = useVehicleController(
    chasisBodyRef,
    wheelsRef,
    wheels
  );

  // TODO: Регистрация контроллера временно отключена для стабильности

  const { scene } = useGLTF(MODELS.CAR);

  // const { accelerateForce, brakeForce, steerAngle } = useControls(
  //   "rapier-dynamic-raycast-vehicle-controller",
  //   {
  //     accelerateForce: { value: 3, min: 0, max: 20 },
  //     brakeForce: { value: 0.1, min: 0, max: 0.5, step: 0.01 },
  //     steerAngle: { value: Math.PI / 24, min: 0, max: Math.PI / 12 },
  //   }
  // );

  // Заменяем значения по умолчанию
  const accelerateForce = 6;
  const brakeForce = 0.5;
  const steerAngle = Math.PI / 24;

  // const { isCameraFollow } = useControls("camera", {
  //   isCameraFollow: { value: true },
  // });

  // Заменяем значение по умолчанию
  const isCameraFollow = true;

  useFrame((state, delta) => {
    if (!chasisMeshRef.current || !vehicleController.current) return;

    const t = 1.0 - Math.pow(0.005, delta);

    /* controls */

    const controller = vehicleController.current;

    const chassisRigidBody = controller.chassis();

    const controls = getKeyboardControls();
    const {
      stop,
      forward,
      backward,
      left,
      right,
      startRace,
      end,
      moveCar,
      resetCar,
      clearReset,
    } = useGame.getState();

    const forwardNumber = Number(controls.forward) || forward;
    const backwardNumber = Number(controls.backward) || backward;
    const leftNumber = Number(controls.left) || left;
    const rightNumber = Number(controls.right) || right;
    const stopNumber =
      (Number(controls.forward) && Number(controls.backward)) || stop;
    // rough ground check
    let outOfBounds = false;

    const translation = chassisRigidBody.translation();
    const ray = new rapier.Ray(translation, {
      x: 0,
      y: -1,
      z: 0,
    });

    const raycastResult = world.castRay(
      ray,
      1,
      false,
      undefined,
      undefined,
      undefined,
      chassisRigidBody
    );

    if (raycastResult) {
      const collider = raycastResult.collider;
      const userData = collider.parent()?.userData;
      outOfBounds = userData?.outOfBounds;
    }

    const engineForce =
      forwardNumber * accelerateForce - backwardNumber * accelerateForce;

    controller.setWheelEngineForce(0, engineForce);
    controller.setWheelEngineForce(1, engineForce);

    // Проверяем, есть ли активный ввод управления
    const hasInput =
      forwardNumber !== 0 ||
      backwardNumber !== 0 ||
      leftNumber !== 0 ||
      rightNumber !== 0;

    // Если есть активный ввод, сбрасываем таймер затухания
    if (hasInput) {
      decelerationTimer.current = 0;
    } else {
      // Если нет активного ввода, увеличиваем таймер затухания
      decelerationTimer.current += delta;
    }

    // Тормоз только если нажаты обе клавиши одновременно (что редко)
    let wheelBrake = stopNumber * brakeForce;

    // Постепенное затухание при отсутствии активного ввода
    if (!hasInput) {
      // Применяем плавное затухание вместо резкого торможения
      if (decelerationTimer.current > 0.1) {
        // Плавно увеличиваем торможение со временем
        const decelerationStrength = Math.min(
          decelerationTimer.current * 2,
          0.3
        );
        wheelBrake = decelerationStrength * brakeForce;
      } else {
        // В первые 0.1 секунды не применяем торможение, только сбрасываем engineForce
        wheelBrake = 0;
      }
    }

    controller.setWheelBrake(0, wheelBrake);
    controller.setWheelBrake(1, wheelBrake);
    controller.setWheelBrake(2, wheelBrake);
    controller.setWheelBrake(3, wheelBrake);

    const currentSteering = controller.wheelSteering(0) || 0;
    const steerDirection = leftNumber - rightNumber;

    const steering = THREE.MathUtils.lerp(
      currentSteering,
      steerAngle * steerDirection,
      0.3
    );

    controller.setWheelSteering(0, steering);
    controller.setWheelSteering(1, steering);

    const forwardAngVel = forwardNumber - backwardNumber;
    const sideAngVel = leftNumber - rightNumber;

    const angvel = _airControlAngVel.set(0, sideAngVel * t, forwardAngVel * t);
    angvel.applyQuaternion(chassisRigidBody.rotation());
    angvel.add(chassisRigidBody.angvel());

    // Проверяем, что значения не NaN
    if (!isNaN(angvel.x) && !isNaN(angvel.y) && !isNaN(angvel.z)) {
      chassisRigidBody.setAngvel(
        new rapier.Vector3(angvel.x, angvel.y, angvel.z),
        true
      );
    }

    // Camera
    if (isCameraFollow) {
      const bodyPosition = chasisBodyRef.current.translation();
      const cameraPosition = new THREE.Vector3();
      cameraPosition.copy(bodyPosition);

      // Для мобильных и планшетов камера выше для лучшего обзора
      const cameraHeight =
        deviceType === "mobile" || deviceType === "tablet" ? 18 : 16;
      const cameraWidth =
        deviceType === "mobile" || deviceType === "tablet" ? 12 : 12;
      cameraPosition.y += cameraHeight;
      cameraPosition.z += cameraWidth;

      const cameraTarget = new THREE.Vector3();
      cameraTarget.copy(bodyPosition);
      cameraTarget.y += 0.25;

      smoothCameraPosition.lerp(cameraPosition, 5 * delta);
      smoothCameraTarget.lerp(cameraTarget, 5 * delta);

      const camera = state.camera;
      camera.position.copy(smoothCameraPosition);
      camera.lookAt(smoothCameraTarget);
    }

    // Сброс позиции при нажатии R, выходе за границы или сбросе из store
    if (controls.reset || outOfBounds || resetCar) {
      const chassis = controller.chassis();
      chassis.setTranslation(new rapier.Vector3(...spawn.position), true);
      const spawnRot = new THREE.Euler(...spawn.rotation);
      const spawnQuat = new THREE.Quaternion().setFromEuler(spawnRot);
      chassis.setRotation(spawnQuat, true);
      chassis.setLinvel(new rapier.Vector3(0, 0, 0), true);
      chassis.setAngvel(new rapier.Vector3(0, 0, 0), true);
      // Сбрасываем флаг сброса после выполнения
      if (resetCar) {
        clearReset();
      }
    }

    if (moveCar) {
      const chassis = controller.chassis();
      const newPos = new rapier.Vector3(...startRacePosition.position);
      chassis.setTranslation(newPos, true);
      const spawnRot = new THREE.Euler(...startRacePosition.rotation);
      const spawnQuat = new THREE.Quaternion().setFromEuler(spawnRot);
      chassis.setRotation(spawnQuat, true);
      chassis.setLinvel(new rapier.Vector3(0, 0, 0), true);
      chassis.setAngvel(new rapier.Vector3(0, 0, 0), true);
      carMovedToStartLocation();
    }
  });

  return (
    <>
      <CarShadow
        width={10}
        height={10}
        opacity={0.3}
        softness={0.7}
        carRigidBodyRef={chasisBodyRef}
      />
      <RigidBody
        position={position}
        rotation={rotation}
        canSleep={false}
        ref={chasisBodyRef}
        colliders={false}
        type="dynamic"
        mass={0.5}
        restitution={0.5}
        friction={0}
        userData={{ isCar: true }}
      >
        <CuboidCollider args={[1.6, 0.2, 0.5]} position={[0, 0, 0]} />
        <CuboidCollider args={[1.6, 0.3, 0.5]} position={[0, 0.6, 0]} />

        {/* chassis */}
        <primitive
          rotation-y={-Math.PI / 2}
          position-y={-0.1}
          scale={0.3}
          object={scene}
          ref={chasisMeshRef}
        />

        {/* wheels */}
        {wheels.map((wheel, index) => (
          <group
            key={index}
            ref={(ref) => (wheelsRef.current[index] = ref)}
            position={wheel.position}
          >
            <group rotation-x={-Math.PI / 2}>
              <mesh>
                <cylinderGeometry
                  args={[radiusWheel, radiusWheel, radiusWheel, 16]}
                />
                <meshStandardMaterial color="#222" />
              </mesh>
            </group>
          </group>
        ))}
      </RigidBody>
    </>
  );
}
